import { Activity, ActivityEntityName, ActivityType, Bill, BulkInvoiceLineValue, CodeFinance, CodeInvoiceStatus, CodeStatus, Company, Contact, ConvertToInvoiceRequest, CreditAmount, DueDateOptions, DueDateType, EmbeddedCode, EmbeddedCodeGroup, EntityLink, Invoice, InvoiceBillingCompany, InvoiceCalculationDomain, InvoiceContact, InvoiceCreatedType, InvoiceLine, InvoiceStatus, InvoiceType, InvoiceViewSummary, InvoicingSummary, InvoicingViewByDays, ItemLinkEntity, Job, LabourCodeId, LineAccounting, PrepaymentSetting, PurchaseOrderLine, SitePro, TaxInclusiveOption, TaxRate, TimesheetTable, WorkOrder } from '../entities';
import { DateUtility } from './date.utility';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { LinkRelationship } from '../enums';
import * as Enumerable from 'linq';
import { AddressUtility } from './address.utility';
import { JobUtility } from './job.utility';

export class InvoiceUtility {


    public static getManualInvoice(request: ConvertToInvoiceRequest): Invoice {
        request.invoiceType = InvoiceType.Invoice;
        request.createdType = InvoiceCreatedType.Default;
        const invoice = this.get<Invoice>(request);
        return invoice;
    }

    public static getInvoiceFromJob(request: ConvertToInvoiceRequest): Invoice {
        request.invoiceType = InvoiceType.Invoice;
        request.createdType = InvoiceCreatedType.FromJob;
        const invoice = this.get<Invoice>(request);
        return invoice;
    }

    public static getInvoiceFromWorkOrder(request: ConvertToInvoiceRequest): Invoice {
        request.invoiceType = InvoiceType.Invoice;
        request.createdType = InvoiceCreatedType.FromWorkOrder;
        const invoice = this.get<Invoice>(request);
        return invoice;
    }

    public static getBill(request: ConvertToInvoiceRequest, isPrepayment: boolean, settings: PrepaymentSetting): Bill {
        if (isPrepayment) {
            request.invoiceType = InvoiceType.PrepaymentSupplier;
        }
        else {
            request.invoiceType = InvoiceType.Bill;
        }

        request = { ...request, company: request.company };
        const bill = this.get<Bill>(request);

        if (isPrepayment) {
            return this.buildBillLinePrepayment(bill, request.taxRate, settings);
        }

        return bill;
    }

    private static buildBillLinePrepayment(bill: Bill, taxRate: TaxRate, settings: PrepaymentSetting): Bill {
        const line: InvoiceLine = {
            _id: undefined,
            name: settings?.name ?? 'Prepayment',
            description: null,
            quantity: 1,
            price: 0,
            subTotal: 0,
            tax: 0,
            total: 0,
            taxRate,
            codeInvoicingStatus: CodeInvoiceStatus.Billable,
        };

        bill.lines = [line];
        return bill;
    }

    public static getInvoiceContact(customer: Contact, site: SitePro): InvoiceContact {
        const { billTo } = customer;
        const credit = billTo?.company?._id !== customer?._id
            ? billTo?.company?.credit
            : customer?.credit;

        const contact = customer ?
            {
                _id: billTo?.company?._id,
                name: billTo?.company?.legalName,
                friendlyName: billTo?.company?.name,
                address: billTo?.site?.address,
                email: billTo?.contact?.email,
                phone: billTo?.contact?.mobile,
                contactId: billTo?.contact?._id,
                contactName: billTo?.contact?.name,
                originalAccount: {
                    _id: customer?._id,
                    name: customer?.name
                },
                type: 'Customer',
                invoiceTheme: site?.invoiceTheme ?? customer?.invoiceTheme ?? null,
                credit: credit,
            } as InvoiceContact
            : null;

        return contact;
    }

    public static getBillContact(technician: Contact): InvoiceContact {
        const contact = technician ?
            {
                _id: technician?.billTo?.company?._id,
                name: technician?.billTo?.company?.legalName,
                address: technician.billTo?.site?.address,
                email: technician.billTo?.contact?.email,
                phone: technician.billTo?.contact?.mobile,
                contactId: technician.billTo?.contact?._id,
                contactName: technician.billTo?.contact?.name,
                friendlyName: technician.billTo?.company?.name,
                originalAccount: {
                    _id: technician._id,
                    name: technician.name
                },
                type: 'Technician',
                billTheme: technician?.billTheme
            } as InvoiceContact
            : null;

        return contact;
    }

    private static get<T extends Bill | Invoice>(request: ConvertToInvoiceRequest): T {

        const { job, workOrder, purchaseOrder, company, taxRate, customer, technician, invoiceType: invoicingType, createdType, codesFinance, labourCode, defaultIncomeTaxRate, site } = request;

        let contact: InvoiceContact = null;
        let lines: InvoiceLine[] = [];
        let links: EntityLink[] = [];
        let taxInclusiveOption = TaxInclusiveOption.Exclusive;

        let timezone = job?.site?.address?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
        const jobAddress = job?.site?.address;

        const billingCompany = InvoiceUtility.buildInvoiceCompany(company);


        switch (invoicingType) {

            case InvoiceType.Bill: {

                contact = this.getBillContact(technician);

                switch (createdType) {

                    case InvoiceCreatedType.FromWorkOrder: {
                        lines = Enumerable
                            .from(workOrder?.codes)
                            .where(x => InvoiceUtility.validBillCode(x, request?.suppliedByContractor))
                            .select(code => {

                                const financeInfo = Enumerable.from(codesFinance).firstOrDefault(x => x?.name === code?.name);

                                const { glCode, codeTaxRate, codeRate } = this.getCodeFinanceDetails(financeInfo, invoicingType, taxRate);

                                const qty = code?.quantity ?? 1;
                                const price = new Decimal(code?.buyPrice ?? 0).toDP(4).toNumber();
                                const lineSubTotal = new Decimal(qty * price).toDP(2).toNumber();
                                const lineTax = new Decimal(lineSubTotal * codeRate).toDP(2).toNumber();
                                const lineTotal = new Decimal(lineSubTotal + lineTax).toDP(2).toNumber();

                                const desc = request?.appendLongDescription && code?.longDescription ? `${code.description} - ${code.longDescription}`.trim() : code.description;
                                return {
                                    name: code.name,
                                    description: desc,
                                    notes: code.note,
                                    location: code.location,
                                    codeStatus: code.status,
                                    quantity: qty,
                                    price: price,
                                    subTotal: lineSubTotal,
                                    tax: lineTax,
                                    total: lineTotal,
                                    uom: code.uom,
                                    codeUoM: code.codeUoM,
                                    itemLink: {
                                        _id: workOrder._id,
                                        name: workOrder.name,
                                        lineId: code._id
                                    },
                                    glCode,
                                    taxRate: codeTaxRate,
                                    type: code?.type,
                                } as InvoiceLine;


                            }).toArray();

                        break;
                    }

                    case InvoiceCreatedType.FromPurchaseOrder: {

                        const { billTo } = customer;

                        contact = customer ?
                            {
                                _id: billTo?.company?._id,
                                name: billTo?.company?.legalName,
                                friendlyName: billTo?.company?.name,
                                address: billTo?.site?.address,
                                email: billTo?.contact?.email,
                                phone: billTo?.contact?.mobile,
                                contactId: billTo?.contact?._id,
                                contactName: billTo?.contact?.name,
                                originalAccount: {
                                    _id: customer?._id,
                                    name: customer?.name
                                },
                                type: 'Customer',
                                invoiceTheme: site?.invoiceTheme ?? customer?.invoiceTheme ?? null
                            } as InvoiceContact
                            : null;

                        links = [
                            ...links,
                            {
                                _id: purchaseOrder._id,
                                name: purchaseOrder.poNumber,
                                type: ActivityEntityName.PurchaseOrder,
                                relationship: LinkRelationship.From
                            } as EntityLink
                        ];

                        const jobLink = purchaseOrder?.links?.find(x => x.type === ActivityEntityName.Job);
                        if (jobLink) {
                            links.push({
                                _id: jobLink._id,
                                name: jobLink.name,
                                type: ActivityEntityName.Job,
                                relationship: LinkRelationship.From
                            } as EntityLink);
                        }

                        lines = Enumerable
                            .from(purchaseOrder?.lines)
                            .where(x => x.createFromCode)
                            .select(line => {

                                const financeInfo = Enumerable.from(codesFinance).firstOrDefault(x => x?.name === line?.name);
                                const { glCode } = this.getCodeFinanceDetails(financeInfo, invoicingType, taxRate);

                                return {
                                    _id: line._id,
                                    name: line.name,
                                    description: line.description,
                                    notes: line.notes,
                                    location: line.location,
                                    codeStatus: CodeStatus.Normal,
                                    quantity: line.quantity,
                                    price: line.price,
                                    subTotal: line.subTotal,
                                    tax: line.tax,
                                    total: line.total,
                                    uom: line.uom,
                                    codeUoM: line.codeUoM,
                                    itemLink: line.itemLink,
                                    glCode,
                                    taxRate: line.taxRate,
                                    type: line.type,
                                } as InvoiceLine;


                            })
                            .toArray();

                        taxInclusiveOption = purchaseOrder.taxInclusiveOption;
                        timezone = company?.address?.timezone ?? timezone;

                        break;
                    }

                    case InvoiceCreatedType.Default: {

                        lines = [];
                        break;

                    }

                }

                break;
            }

            case InvoiceType.Invoice: {

                contact = this.getInvoiceContact(customer, site);
                // const { billTo } = customer;

                // contact = customer ?
                //     {
                //         _id: billTo?.company?._id,
                //         name: billTo?.company?.legalName,
                //         friendlyName: billTo?.company?.name,
                //         address: billTo?.site?.address,
                //         email: billTo?.contact?.email,
                //         phone: billTo?.contact?.mobile,
                //         contactId: billTo?.contact?._id,
                //         contactName: billTo?.contact?.name,
                //         originalAccount: {
                //             _id: customer?._id,
                //             name: customer?.name
                //         },
                //         type: 'Supplier',
                //         invoiceTheme: customer?.invoiceTheme ?? null
                //     } as InvoiceContact
                //     : null;



                switch (createdType) {

                    case InvoiceCreatedType.FromJob: {
                        //Need to make sure that we only add the labour code once
                        let labourCodeAdded = false;
                        lines = Enumerable
                            .from(job.workOrders)
                            .select(w => {
                                let codes = [];
                                if (labourCodeAdded) {
                                    codes = w?.codes;
                                }
                                else {
                                    labourCodeAdded = true;
                                    //Add the labour code as the first code
                                    codes = labourCode ? [labourCode, ...(w?.codes ?? [])] : w?.codes;
                                }

                                return Enumerable.from(codes)
                                    .where(x => InvoiceUtility.validInvoiceCode(x))
                                    .select(code => {

                                        const itemLink = {
                                            _id: w._id,
                                            name: w.name,
                                            lineId: code._id
                                        } as ItemLinkEntity;

                                        return this.createInvoiceLine(code, codesFinance, invoicingType, taxRate, itemLink, defaultIncomeTaxRate, request.appendLongDescription);

                                    }).toArray();
                            })
                            .selectMany(x => [...x])
                            .toArray();
                        break;
                    }

                    case InvoiceCreatedType.FromWorkOrder: {

                        lines = Enumerable
                            .from(job.workOrders)
                            .where(x => x._id === workOrder._id)
                            .selectMany(w => {

                                const codes = labourCode
                                    ? [labourCode, ...(w?.codes ?? [])]
                                    : w?.codes

                                return codes;
                            })
                            .where(x => InvoiceUtility.validInvoiceCode(x))
                            .select(code => {

                                const itemLink = {
                                    _id: workOrder._id,
                                    name: workOrder.name,
                                    lineId: code._id
                                } as ItemLinkEntity;

                                return this.createInvoiceLine(code, codesFinance, invoicingType, taxRate, itemLink, defaultIncomeTaxRate, request.appendLongDescription);
                            }).toArray();

                        break;
                    }

                    case InvoiceCreatedType.Default: {

                        lines = [];

                        break;
                    }
                }

                break;
            }
        }

        let subTotal = 0;
        let tax = 0;
        let total = 0;

        Enumerable
            .from(lines)
            .forEach(l => {
                subTotal += l.subTotal;
                tax += l.tax;
                total += l.total;
            });

        const now = new Date();
        const today = DateUtility.getDateTime(now, timezone).startOf('day');

        const account = invoicingType === InvoiceType.Bill
            ? technician
            : customer;

        const dueDate = this.getDueDateFromAccount(account?.credit?.dueDate, today).toJSDate();

        const invoice = {
            _id: null,
            active: true,
            status: InvoiceStatus.Draft,
            invoiceDate: today.toJSDate(),
            generatedDate: now,
            dueDate,
            lines,
            contact,
            billingCompany: billingCompany,
            customerReference: job?.customerWO,
            job: job ? { _id: job._id, name: job.jobNumber } : null,
            workOrder: workOrder?._id ? { _id: workOrder._id, name: workOrder.workOrderNumber } : null,
            subTotal: new Decimal(subTotal).toDP(2).toNumber(),
            tax: new Decimal(tax).toDP(2).toNumber(),
            total: new Decimal(total).toDP(2).toNumber(),
            taxInclusiveOption,
            terms: billingCompany?.invoiceDetails,
            notes: null,
            taxRate,
            timezone,
            jobAddress,
            timesheetIds: labourCode?.timesheetIds ?? [],
            theme: invoicingType === InvoiceType.Bill
                ? contact?.billTheme
                : contact?.invoiceTheme,
            links
        } as Invoice;

        if (invoicingType === InvoiceType.Bill) {

            const bill = {
                ...invoice,
                workOrder: workOrder
                    ? { _id: workOrder._id, name: workOrder.workOrderNumber }
                    : null,
                customerReference: workOrder?.workOrderNumber ?? null,
                theme: contact?.billTheme
            };

            if (createdType === InvoiceCreatedType.FromPurchaseOrder) {
                bill.customerReference = purchaseOrder?.reference ?? null;
            };

            return bill as T;
        }

        return invoice as T;
    }

    public static createInvoiceLine(code: EmbeddedCode, codesFinance: CodeFinance[], invoicingType: InvoiceType, taxRate: TaxRate, itemLink: ItemLinkEntity, defaultIncomeTaxRate: TaxRate, appendLongDescription: boolean = false): InvoiceLine {
        const financeInfo = codesFinance?.find(x => x.name === code.name);
        const { glCode, codeTaxRate, codeRate } = this.getCodeFinanceDetails(financeInfo, invoicingType, taxRate);

        const noCharge = code.codeInvoicingStatus === CodeInvoiceStatus.NoCharge;
        const qty = code?.quantity ?? 1;
        const price = noCharge
            ? 0
            : new Decimal(code?.sellPrice ?? 0).toDP(4).toNumber();
        const lineSubTotal = new Decimal(qty * price).toDP(2).toNumber();
        const lineTax = new Decimal(lineSubTotal * codeRate).toDP(2).toNumber();
        const lineTotal = new Decimal(lineSubTotal + lineTax).toDP(2).toNumber();
        const desc = appendLongDescription && code?.longDescription ? `${code.description} - ${code.longDescription}`.trim() : code.description;

        const line = {
            name: code.name,
            description: desc,
            notes: code.note,
            location: code.location,
            codeStatus: code.status,
            quantity: qty,
            price: price,
            subTotal: lineSubTotal,
            tax: lineTax,
            total: lineTotal,
            uom: code.uom,
            codeUoM: code.codeUoM,
            itemLink: itemLink,
            glCode,
            taxRate: codeTaxRate,
            type: code?.type
        } as InvoiceLine;

        if (code?._id === LabourCodeId) {
            line.taxRate = defaultIncomeTaxRate;
        }


        return line;
    }

    public static getDueDateFromAccount(dueDateOptions: DueDateOptions, relativeDate: DateTime): DateTime {

        //Default behaviour is last day of next month
        const method = dueDateOptions?.type ?? DueDateType.XDayFollowingMonth;
        const days = dueDateOptions?.value ?? 31;
        let dueDate = relativeDate;
        switch (method) {
            case DueDateType.DueOnReceipt:
                {
                    dueDate = relativeDate;
                    break;
                }
            case DueDateType.XDayAfterEndOfBillInvoiceMonth:
                {
                    dueDate = dueDate.endOf('month').plus({ day: days }).startOf('day'); //.toJSDate();
                    break;
                }
            case DueDateType.XDayAfterTheBillInvoiceDate:
                {
                    dueDate = dueDate.plus({ day: days }); //.toJSDate();
                    break;
                }
            case DueDateType.XDayCurrentMonth:
                {
                    const month = dueDate.month;
                    const year = dueDate.year;
                    const currentDay = dueDate.day;
                    const safeDay = DateUtility.getValidDayForMonthAndYear(days, month, year);
                    //This mode could result in a due date prior to invoice date - prevent that
                    dueDate = dueDate.set({ day: safeDay > currentDay ? safeDay : currentDay });
                    break;
                }
            case DueDateType.XDayFollowingMonth:
                {
                    dueDate = dueDate.endOf('month').plus({ day: 1 }).startOf('day');
                    const month = dueDate.month;
                    const year = dueDate.year;
                    const safeDay = DateUtility.getValidDayForMonthAndYear(days, month, year);
                    dueDate = dueDate.set({ day: safeDay });
                    break;
                }
            default:
                {
                    dueDate = dueDate.endOf('month').plus({ month: 1 }).startOf('day');
                    break;
                }
        }
        return dueDate;
    }

    public static getDefaultLine(): InvoiceLine {
        return {
            name: null,
            description: null,
            quantity: 1,
            notes: null,
            price: 0,
            tax: 0,
            subTotal: 0,
            total: 0,
            codeUoM: null,
            glCode: null,
            taxRate: null,
        } as InvoiceLine;
    }

    public static getInvoiceSummary(invoice: Invoice | Bill): InvoicingSummary {

        let invoiceSummary: InvoicingSummary = {
            _id: invoice._id,
            type: invoice.type,
            address: invoice?.jobAddress ?? invoice.contact?.address,
            jobAddress: invoice?.jobAddress,
            status: invoice.status,
            contact: invoice.contact?.name,
            invoiceNumber: invoice.invoiceNumber,
            invoiceDate: invoice.invoiceDate,
            dueDate: invoice.dueDate,
            total: invoice.total,
            subTotal: invoice.subTotal,
            version: invoice.version,
            job: invoice?.job?.name,
            integration: {
                success: invoice?.integration?.success,
                type: invoice?.integration?.type,
                integrationInvoiceId: invoice?.integration?.integrationInvoiceId
            },
            company: undefined,
            billingCompany: undefined,
            prepaymentSubTotal: invoice.prepaymentSubTotal,
            prepaymentTotal: invoice.prepaymentTotal,
            creditAmount: invoice.creditAmount,
            amountPaid: invoice.amountPaid,
        };

        return invoiceSummary;
    }

    private static getCodeFinanceDetails(financeInfo: CodeFinance, invType: InvoiceType, defaultTaxRate: TaxRate) {

        let glCode: string = null;
        let codeTaxRate: TaxRate = null;
        let codeRate: number = 0;

        if (invType == InvoiceType.Bill) {
            glCode = financeInfo?.itemPurchased ? financeInfo?.purchaseGlCode : null;
            codeTaxRate = (financeInfo?.itemPurchased ? financeInfo?.purchaseTaxRate : null) ?? defaultTaxRate;
        }
        else if (invType == InvoiceType.Invoice) {
            glCode = financeInfo?.itemSold ? financeInfo?.saleGlCode : null;
            codeTaxRate = (financeInfo?.itemSold ? financeInfo?.saleTaxRate : null) ?? defaultTaxRate;
        }
        //If we have an empty string for the glCode, force it to be null
        if (!glCode)
            glCode = null;
        codeRate = (codeTaxRate?.rate ?? 0) / 100;
        return {
            glCode,
            codeTaxRate,
            codeRate
        };
    }

    public static getDefaultInvoicing(): any {

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const today = DateUtility.getDateTime(new Date(), timezone).startOf('day');

        const defaultData: Partial<Invoice | Bill> = {
            _id: null,
            invoiceNumber: null,
            invoiceDate: today.toJSDate(),
            dueDate: today.endOf('month').plus({ day: 1 }).endOf('month').toJSDate(),
            contact: null,
            customerReference: null,
            notes: null,
            integration: null,
            job: null,
            workOrder: null,
            taxInclusiveOption: TaxInclusiveOption.Exclusive,
            billingCompany: null,
            status: InvoiceStatus.Draft,
            createdBy: null,
            taxRate: null,
            attachments: [],
            terms: null,
            timezone: timezone,
            lines: [],
            subTotal: 0,
            tax: 0,
            total: 0,

            amountPaid: 0,
            lastPaymentDate: null,
            isReconcile: false,
            roundingAmount: 0,
        };
        return defaultData;
    }

    public static getDefaultViews(): InvoiceViewSummary[] {

        const defaultViews = [];

        defaultViews.push({
            _id: InvoicingViewByDays.Last30Days,
            name: InvoicingViewByDays.Last30Days,
            isDefault: true,
            system: true,
            selected: false,
            ownerId: null,
            filters: {
                dayRange: InvoicingViewByDays.Last30Days
            }
        } as InvoiceViewSummary);

        defaultViews.push({
            _id: InvoicingViewByDays.Last60Days,
            name: InvoicingViewByDays.Last60Days,
            isDefault: false,
            system: true,
            selected: false,
            ownerId: null,
            filters: {
                dayRange: InvoicingViewByDays.Last60Days
            }
        } as InvoiceViewSummary);

        defaultViews.push({
            _id: InvoicingViewByDays.Last90Days,
            name: InvoicingViewByDays.Last90Days,
            isDefault: false,
            system: true,
            selected: false,
            ownerId: null,
            filters: {
                dayRange: InvoicingViewByDays.Last90Days
            }
        } as InvoiceViewSummary);

        defaultViews.push({
            _id: InvoicingViewByDays.Last90PlusDays,
            name: InvoicingViewByDays.Last90PlusDays,
            isDefault: false,
            system: true,
            selected: false,
            ownerId: null,
            filters: {
                dayRange: InvoicingViewByDays.Last90PlusDays
            }
        } as InvoiceViewSummary);

        return defaultViews;
    }

    public static formatLinesPrice(lines: InvoiceLine[] = []): InvoiceLine[] {

        lines = Enumerable.from(lines).select(x => {
            x.price = Math.abs(new Decimal(x?.price ?? 0).toDP(4).toNumber());

            return x;
        }).toArray()

        return lines;

    }

    public static calculatePaymentAmount(total = 0, amountPaid = 0, isReconcile = false): { outstandingAmount: number, roundingAmount: number } {
        let outstandingAmount = 0;
        let roundingAmount = 0;
        total = total || 0;
        amountPaid = amountPaid || 0;
        const diffAmount = new Decimal(total - amountPaid).toDP(2).toNumber();

        if (isReconcile) {
            roundingAmount = diffAmount;
            outstandingAmount = 0;
        } else {
            roundingAmount = 0;
            outstandingAmount = diffAmount;
        }

        return { outstandingAmount, roundingAmount };
    }


    public static getInvoiceLineTotal(lines: InvoiceLine[], defaultTaxRate: TaxRate, taxInclusiveOption: TaxInclusiveOption): LineAccounting {


        let subTotal = new Decimal(0);
        let tax = new Decimal(0);
        let total = new Decimal(0);

        Enumerable.from(lines)
            .forEach(line => {

                const rate = this.getTaxRate(line.taxRate, defaultTaxRate);

                const qty = line.quantity ?? 1;

                const price = new Decimal(line?.price ?? 0).toDP(4).toNumber();

                const lineSubTotal = new Decimal(qty * price).toDP(2).toNumber();

                let lineTax = 0;
                let lineTotal = 0;

                switch (taxInclusiveOption) {
                    case TaxInclusiveOption.Exclusive:
                        lineTax = new Decimal(Number(lineSubTotal * rate)).toDP(2).toNumber();
                        lineTotal = new Decimal(lineSubTotal + lineTax).toDP(2).toNumber();
                        break;

                    case TaxInclusiveOption.Inclusive:
                        const taxPlus = 1 + rate;
                        lineTax = new Decimal((lineSubTotal / taxPlus) * rate).toDP(2).toNumber();
                        lineTotal = lineSubTotal;
                        break;
                }

                line.subTotal = lineSubTotal;
                line.tax = lineTax;
                line.total = lineTotal;

                subTotal = subTotal.plus(line.subTotal);
                tax = tax.plus(line.tax);
                total = total.plus(line.total);
            });

        return {
            lines: lines,
            subTotal: subTotal.toDP(4).toNumber(),
            tax: tax.toDP(4).toNumber() ?? 0,
            total: total.toDP(4).toNumber() ?? 0
        };
    }

    ///Need to review the rounding issues when working backwards from the tax inclusive option
    //Theres a difference where we are using this to calculate the ex-tax unit price
    public static calculateTax(subTotal: number, taxRate: TaxRate, taxInclusiveOption: TaxInclusiveOption, defaultTaxRate: TaxRate) {
        const rate = this.getTaxRate(taxRate, defaultTaxRate);
        let lineTax = 0;
        switch (taxInclusiveOption) {
            case TaxInclusiveOption.Exclusive:
                lineTax = new Decimal(Number(subTotal * rate)).toDP(2).toNumber();
                break;

            case TaxInclusiveOption.Inclusive:
                const taxPlus = 1 + rate;
                lineTax = new Decimal((subTotal / taxPlus) * rate).toDP(2).toNumber();
                break;
        }
        return lineTax;
    }

    public static calculateExTaxUnitPrice(unitPrice: number, taxRate: TaxRate, taxInclusiveOption: TaxInclusiveOption, defaultTaxRate: TaxRate) {
        const rate = this.getTaxRate(taxRate, defaultTaxRate);
        let exTaxUnitPrice = 0;
        switch (taxInclusiveOption) {
            case TaxInclusiveOption.Exclusive:
                exTaxUnitPrice = unitPrice;
                break;
            case TaxInclusiveOption.Inclusive:
                const taxPlus = 1 + rate;
                const lineTax = new Decimal((unitPrice / taxPlus) * rate).toDP(4).toNumber();
                exTaxUnitPrice = unitPrice - lineTax;
                break;
        }
        return exTaxUnitPrice;
    }

    public static getPurchaseOrderLineTotal(lines: PurchaseOrderLine[], defaultTaxRate: TaxRate, taxInclusiveOption: TaxInclusiveOption, markupPercent?: number): LineAccounting {


        const markupRate = 0; // markupPercent ? markupPercent / 100 : 0;

        let tax = 0;
        let subTotal = 0;
        let total = 0;
        let supplierMarkup = 0;

        Enumerable.from(lines)
            .forEach(line => {
                const rate = this.getTaxRate(line.taxRate, defaultTaxRate);

                const qty = line.quantity ?? 1;

                const price = new Decimal(+line?.price).toDP(4).toNumber();

                const lineSubTotal = new Decimal(qty * price).toDP(2).toNumber();;

                let lineTax = 0;
                let lineTotal = 0;
                let lineMarkup = 0;

                switch (taxInclusiveOption) {
                    case TaxInclusiveOption.Exclusive:
                        lineTax = Number(lineSubTotal * rate);
                        lineTotal = lineSubTotal + lineTax;
                        break;

                    case TaxInclusiveOption.Inclusive:
                        const taxPlus = 1 + rate;
                        lineTax = (lineSubTotal / taxPlus) * rate;
                        lineTotal = lineSubTotal;
                        break;
                }

                lineMarkup = Number(lineSubTotal * markupRate);
                // add code
                line.subTotal = lineSubTotal;
                line.tax = lineTax;
                line.total = lineTotal;

                supplierMarkup += lineMarkup;
                subTotal += lineSubTotal;
                tax += lineTax;
                total += lineTotal + lineMarkup;
            });


        return {
            lines: lines,
            supplierMarkup: supplierMarkup ?? 0,
            subTotal: subTotal ?? 0,
            tax: tax ?? 0,
            total: total ?? 0
        };
    }

    public static sortedInvoiceActivities(activities: Activity[]): Activity[] {

        const sorted = Enumerable.from(activities)
            .orderByDescending(x => x.createdDate)
            .thenBy(x => {

                switch (x.type) {
                    case ActivityType.JobBilled:
                        return 6;
                    case ActivityType.InvoiceCreated:
                        return 5;
                    case ActivityType.InvoiceApproved:
                        return 4;
                    case ActivityType.InvoiceReissue:
                        return 3;
                    case ActivityType.InvoiceError:
                        return 2;
                    case ActivityType.InvoiceVoid:
                        return 1;
                }

            }).toArray();

        return sorted;

    }

    public static sortedBillActivities(activities: Activity[]): Activity[] {

        const sorted = Enumerable.from(activities)
            .orderBy(x => x.createdDate)
            .thenBy(x => {

                switch (x.type) {
                    case ActivityType.WorkOrderBill:
                        return 6;
                    case ActivityType.BillCreated:
                        return 5;
                    case ActivityType.BillApproved:
                        return 4;
                    case ActivityType.BillReissue:
                        return 3;
                    case ActivityType.BillError:
                        return 2;
                    case ActivityType.BillVoid:
                        return 1;
                }

            }).toArray();

        return sorted;

    }

    public static getCreditAmount(invoices: Invoice[]): CreditAmount {

        if (!invoices?.length) {
            return {
                outstanding: 0,
                overdue: 0
            } as CreditAmount;
        }

        let total = 0;
        let payments = 0;
        let rounding = 0;
        let overdueTotal = 0;
        let overduePayments = 0;
        let overdueRounding = 0;



        Enumerable
            .from(invoices)
            .forEach(x => {
                total += x?.total ?? 0;
                payments += x?.amountPaid ?? 0;
                rounding += x?.roundingAmount ?? 0;

                const timezone = x?.jobAddress?.timezone ?? x?.timezone;
                const dueDate = DateUtility.getDateTime(x.dueDate, timezone);
                const today = DateUtility.getDateTime(new Date(), timezone).startOf('day');

                if (dueDate < today) {
                    overdueTotal += x?.total ?? 0;
                    overduePayments += x?.amountPaid ?? 0;
                    overdueRounding += x?.roundingAmount ?? 0;
                }
            });

        return {
            outstanding: new Decimal((total - (payments + rounding))).toDP(2).toNumber(),
            overdue: new Decimal((overdueTotal - (overduePayments + overdueRounding))).toDP(2).toNumber()
        } as CreditAmount;

    }

    private static validInvoiceCode(code: EmbeddedCode, options?: { bypassInvoiceCheck: boolean }): boolean {

        if (code?.status === CodeStatus.Cancelled)
            return false;

        if (code?.codeInvoicingStatus === CodeInvoiceStatus.NotBillable)
            return false;

        if (!options?.bypassInvoiceCheck && code?.invoice)
            return false;

        //Do not add a labour code with a zero total
        if (code.type === "LABOUR" && code.sellPrice == 0)
            return false;

        return true;
    }


    public static validBillCode(code: EmbeddedCode, suppliedByContractor: boolean = true): boolean {

        if (code?.status === CodeStatus.Cancelled)
            return false;

        if (suppliedByContractor && !code?.suppliedBySubcontractor)
            return false;

        if (!suppliedByContractor && code?.suppliedBySubcontractor)
            return false;

        if (code.bill)
            return false;

        if (!!code?.purchaseOrder?._id)
            return false;

        return true;

    }

    public static getTaxRate(lineTax: TaxRate, defaultTax: TaxRate): number {
        const rate = lineTax?.rate ?? defaultTax?.rate ?? 0;
        return rate / 100;
    }


    public static updateInvoices(invoice: Invoice, items: InvoicingSummary[]): InvoicingSummary[] {

        const index = Enumerable.from(items).indexOf((x) => x._id === invoice._id);

        const newItem = this.getInvoiceSummary(invoice);
        const item: any = {
            ...newItem,
            //  contact: invoice.contact.friendlyName,
            contactId: invoice.contact._id,
            job: invoice.job?.name,
            workOrder: invoice.workOrder?.name
        }

        const newItems = index === -1 ? [...items, item] : [...items.slice(0, index), item, ...items.slice(index + 1)];
        return newItems;
    }

    public static getFinanceToolTip(item: EmbeddedCodeGroup): string {

        if (item?.invoiceApproved && item?.billApproved) {
            return `Invoice/Bill ${item.invoice.name}/${item.bill.name} is linked to this item. Editing is locked.`;
        }

        if (item?.invoiceApproved) {
            return `Invoice ${item.invoice.name} is linked to this item. Editing is locked.`;
        }

        if (item?.billApproved) {
            return `Bill ${item.bill.name} is linked to this item. Editing is locked.`;
        }

        return "";
    }

    public static getTimesheetFinanceToolTip(item: TimesheetTable): string {

        if (item?.invoiceApproved) {
            return `Invoice ${item.invoice.name} is linked to this item. Editing is locked.`;
        }

        return "";
    }

    public static getFinanceRecordRights<T extends Bill | Invoice>(record: T, canManageRecord: boolean, canManageApprovedRecord: boolean, canAccessPayment: boolean, canManagePayment: boolean): FinanceRecordRights {

        const draft = record?.status === InvoiceStatus.Draft;
        const approved = record?.status === InvoiceStatus.Approved;
        const typePrepayment = [InvoiceType.PrepaymentInvoice, InvoiceType.PrepaymentSupplier].includes(record?.type);
        const typeCreditNote = [InvoiceType.CreditNote, InvoiceType.CreditNoteSupplier].includes(record?.type) || record?.total < 0;

        const noPayments = !record?.hasPayments
        const noAllocation = !record?.hasAllocation

        const showButtonSync = canAccessPayment && canManagePayment && approved && !typePrepayment; // TODO: maybe enable sync prepayment invoice later, show when paid amount == prepayment total
        const showButtonEdit = ((canManageRecord && draft) || (canManageRecord && approved && canManageApprovedRecord && noPayments && noAllocation))
            && !typePrepayment; // TODO: enable edit prepayment invoice, can change the lines but the prepayment total should be the same
        const showButtonProcessing = canManageRecord && noPayments;
        const showButtonReissue = approved && !typePrepayment;
        const showTabPayment = canAccessPayment && !draft;

        const showUpdateAndRecalculate = showButtonEdit && !typeCreditNote;
        const showEditForPrepayment = typePrepayment && noPayments && noAllocation;


        const rights: FinanceRecordRights = {
            showButtonSync,
            showButtonEdit,
            showButtonProcessing,
            showButtonReissue,
            showTabPayment,
            showUpdateAndRecalculate,
            showEditForPrepayment: showEditForPrepayment,
        };

        return rights;
    }

    public static canEditCustomer(invoice: Invoice, canManageApproved: boolean) {
        const domain = InvoiceCalculationDomain.from(invoice, false);
        const isDraft = invoice.status === InvoiceStatus.Draft;
        const isApproved = invoice.status === InvoiceStatus.Approved;
        const isValidType = domain.isType(InvoiceType.Invoice) || domain.isType(InvoiceType.CreditNote);

        return isValidType && (isDraft || (isApproved && canManageApproved));
    }

    public static recalculateCreditNote(item: Invoice | Bill, isBill: boolean) {
        if (!item) {
            return item;
        }

        if (item.type === InvoiceType.CreditNote || item.type === InvoiceType.CreditNoteSupplier) {
            for (const line of item.lines) {

                if (line.price < 0) {
                    line.price = Math.abs(line.price);
                }

                if (line.quantity > 0) {
                    line.quantity = line.quantity * -1;
                }
                else {
                    line.quantity = Math.abs(line.quantity);
                }
            }

            const totalModel = InvoiceUtility.getInvoiceLineTotal(item.lines, item.taxRate, item.taxInclusiveOption);
            const domain = InvoiceCalculationDomain.from({ ...item, ...totalModel }, isBill);

            const calculated = domain.calculateAmounts();
            item = {
                ...item,
                ...totalModel,
                ...calculated,
            } as any
        }

        return item;
    }

    /**
     * add un-invoiced items from job to invoice
     * update unvoiced items
     * update invoice total, subTotal, tax
     * update invoice billing company
     * update labour line
     */
    public static updateAndRecalculateInvoice(options: {
        invoice: Invoice,
        job: Job,
        company: Company,
        codesFinance: CodeFinance[],
        labourCode: EmbeddedCode,
        appendLongDescription?: boolean
    }): Invoice {

        const { invoice, job, labourCode, company, codesFinance, appendLongDescription } = options;

        const stageId = invoice.workOrder?._id;
        const jobId = invoice.job?._id;
        const stage = job?.workOrders?.find(x => x._id === stageId);

        if (!jobId)
            return invoice;


        const laborLink: ItemLinkEntity = stage
            ? {
                _id: stage._id,
                name: stage.name,
                lineId: labourCode?._id
            }
            : {
                _id: jobId,
                name: job.name,
                lineId: labourCode?._id
            };

        const laborLine = labourCode
            ? this.createInvoiceLine(labourCode, codesFinance, InvoiceType.Invoice, invoice.taxRate, laborLink, invoice.taxRate, appendLongDescription)
            : null;

        const codeMap = new Map<string, EmbeddedCode>();

        const newLines = Enumerable
            .from(job.workOrders)
            .where(x => {
                return stageId
                    ? x._id === invoice.workOrder._id
                    : true;
            })
            .select(stage => {

                return Enumerable
                    .from(stage.codes)
                    .doAction(x => {
                        if (x.invoice?._id === invoice._id)
                            codeMap.set(x._id, x);
                    })
                    .where(x => InvoiceUtility.validInvoiceCode(x))
                    .select(code => {
                        const itemLink: ItemLinkEntity = {
                            _id: stage._id,
                            name: stage.name,
                            lineId: code._id
                        };

                        const invoiceLine = this.createInvoiceLine(code, codesFinance, InvoiceType.Invoice, invoice.taxRate, itemLink, invoice.taxRate, appendLongDescription);
                        return invoiceLine;
                    })
                    .toArray();
            })
            .selectMany(x => x)
            .toArray();

        const oldLine = Enumerable
            .from(invoice.lines)
            .where(x => x.type !== 'LABOUR')
            .select(x => {
                if (!x.itemLink) {
                    return x;
                }

                const code = codeMap.get(x.itemLink.lineId);

                if (code.status === CodeStatus.Cancelled)
                    return null;

                if (code.codeInvoicingStatus === CodeInvoiceStatus.NotBillable)
                    return null;

                const invoiceLine = this.createInvoiceLine(code, codesFinance, InvoiceType.Invoice, invoice.taxRate, x.itemLink, invoice.taxRate, appendLongDescription);
                return invoiceLine;
            })
            .where(x => !!x)
            .toArray();


        const lines = [
            laborLine,
            ...oldLine,
            ...newLines,
        ].filter(x => !!x);

        const { total, subTotal, tax } = InvoiceUtility.getInvoiceLineTotal(lines, invoice.taxRate, invoice.taxInclusiveOption);

        const updatedInvoice: Invoice = {
            ...invoice,
            lines,
            total,
            subTotal,
            tax,
            timesheetIds: labourCode?.timesheetIds ?? invoice.timesheetIds,
        }

        if (company) {
            updatedInvoice.billingCompany = InvoiceUtility.buildInvoiceCompany(company);
        }

        return updatedInvoice;

    }

    public static buildInvoiceCompany(company: Company): InvoiceBillingCompany {

        if (!company) return null;

        return <InvoiceBillingCompany>{
            _id: company._id,
            name: company.name,
            address: company.address,
            phone: company.phone,
            email: company.email,
            taxId: company.taxId,
            invoiceDetails: company.invoiceDetails
        };
    }
}

export interface FinanceRecordRights {
    showButtonSync: boolean;
    showButtonEdit: boolean;
    showButtonProcessing: boolean;
    showButtonReissue: boolean;
    showTabPayment: boolean;
    showUpdateAndRecalculate: boolean;
    showEditForPrepayment: boolean;
}
